import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Col, notification, Row } from 'antd';
import _ from 'lodash';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import {
	CustomerFacility,
	OrderProduct,
	Product,
	ProductSize,
} from '../../../../../api-client';
import { RootState } from '../../../../../application/store';
import BVComboBox from '../../../../../common/components/BVControls/BVComboBox/bv-combo-box';
import BVDatePicker from '../../../../../common/components/BVControls/BVDatePicker/bv-date-picker';
import BVInput from '../../../../../common/components/BVControls/BVInput/bv-input';
import BVInputNumber from '../../../../../common/components/BVControls/BVInputNumber/bv-input-number';
import BVShimmer from '../../../../../common/components/BVShimmers/BVShimmer/bv-shimmer';
import { useOrder } from '../../../../../common/hooks/useOrder';
import { useProduct } from '../../../../../common/hooks/useProduct';
import { keepProduct } from '../../../../../slices/OrderSummary/orderSummarySlice';
import {
	formatStandardDate,
	momentFormat,
} from '../../../../../utils/data-format-enchanters/datetime-enchanter';
import {
	generateProgressiveValue,
	orderTotalBySizes,
} from '../../../../../utils/data-format-enchanters/order-products-enchanter';
import { useForm } from '../../hooks/useForm';
import './bv-compact-form.scss';

interface IBVCompactForm {
	businessUnitCode: string;
	product?: Product;
	productSizes?: ProductSize[];
	productSizesLoading: boolean;
	isEmbedded: boolean;
	productIndex?: number;
	isNewHover?: boolean;
	hoveredProduct?: Product;
}

const highEndTester = /HE Jewelry Watches|HE Complicated Watches/;

const BVCompactForm: React.FunctionComponent<IBVCompactForm> = ({
	businessUnitCode,
	product,
	productSizes,
	productSizesLoading,
	isEmbedded,
	productIndex,
	isNewHover,
	hoveredProduct,
}) => {
	const { autosaveOrderForCurrentCustomer, upsertOrderLoading } = useOrder();

	const {
		handleFacilities,
		facilities,
		facilitiesLoading,
		handleAvailableDate,
		availableDate,
		availableDateLoading,
	} = useProduct();

	const { handleInitialize, handleChange, handleChangeMultiple, formValues } =
		useForm();

	const history = useHistory();
	const bvCustomer = useSelector((state: RootState) => state.customer);
	const [isToShowSelect, setIsToShowSelect] = useState<boolean>(false);
	const [isToShowDatePicker, setIsToShowDatePicker] = useState<boolean>(false);
	const [isSelectOnFocus, setIsSelectOnFocus] = useState<boolean>(false);

	useEffect(() => {
		if (!isEmbedded && bvCustomer.id) handleFacilities(bvCustomer.id);
	}, []);

	useEffect(() => {
		setIsToShowSelect(false);
		setIsToShowDatePicker(false);

		if (product?.id === hoveredProduct?.id)
			handleFacilities(Number(bvCustomer.id));
	}, [isNewHover]);

	const isEcc = () => {
		return highEndTester.test(product?.business || '')
			? bvCustomer.isEccHighEnd
			: bvCustomer.isEcc;
	};

	const _handleResetForm = (facility: CustomerFacility) => {
		if (
			facilities &&
			facilities.filter((item) => item.ecc === isEcc()).length > 0
		) {
			handleInitialize({
				facility:
					Object.keys(facility).length > 0
						? facility
						: facilities.filter((item) => item.ecc === isEcc())[0],
				sizes:
					_.map(productSizes, (dataItem) => ({ ...dataItem, quantity: 1 })) ||
					[],
				subTotal: 0,
			});
		}
	};

	useEffect(() => {
		if (!isEmbedded || product?.id === hoveredProduct?.id)
			_handleResetForm(formValues.facility || {});
	}, [productSizes, facilities]);

	useEffect(() => {
		if (
			product &&
			product.id &&
			formValues.facility &&
			(!isEmbedded || product.id === hoveredProduct?.id)
		)
			handleAvailableDate(product.id, formValues.facility.id);
	}, [product, formValues.facility]);

	const _handleFacilityAvailableDate = () => {
		if (!formValues.facility || !availableDate) {
			return '';
		}

		return `Available for ${formValues.facility.name} from ${formatStandardDate(availableDate)}`;
	};

	const dispatch = useDispatch();

	const bvOrderSummary = useSelector((state: RootState) => state.orderSummary);

	const _handleSubmitProduct = () => {
		if (product) {
			let orderProduct: OrderProduct = {
				id: product.id,
				rowId: generateProgressiveValue(bvOrderSummary.products),
				businessUnitCode: businessUnitCode,
				catalogCode: product.catalogCode,
				imageUrl: product.image && product.image.galleryUrl,
				name: product.descrShort,
				family: product.family,
				// Form-Properties
				facility: formValues.facility,
				limitedEditionCode: formValues.limitedEditionCode,
				sizes: formValues.sizes,
			};
			console.log(orderProduct);

			if (formValues.deliveryDate) {
				orderProduct.deliveryDate = new Date(
					formValues.deliveryDate.startOf('day'),
				).toISOString();
			}

			dispatch(keepProduct(orderProduct));

			notification.success({
				message: 'Product saved!',
				description: 'Product successfully saved.',
				duration: 2,
			});

			_handleResetForm(formValues.facility || {});

			autosaveOrderForCurrentCustomer();
		}
	};

	const _handleFacilityExistingDates = () => {
		if (!formValues.facility) {
			return null;
		}

		let existingOrderProducts = _.filter(
			bvOrderSummary.products,
			(entry) => entry.facility?.id === formValues.facility?.id,
		);
		if (existingOrderProducts.length === 0) {
			return null;
		}

		let existingDates = _.uniqBy(
			_.map(existingOrderProducts, (dataItem) => dataItem.deliveryDate),
			(entry) => entry,
		);

		return _.sortBy(existingDates, (entry) => entry).map(
			(existingDate, dateIndex) =>
				existingDate &&
				moment(existingDate) >
					moment(availableDate).subtract(1, 'days').endOf('day') && (
					<span
						key={dateIndex}
						className="compact-shipping-existing-dates"
						onClick={() =>
							handleChange(
								'deliveryDate',
								moment(formatStandardDate(existingDate), momentFormat),
							)
						}
					>
						{formatStandardDate(existingDate)}
					</span>
				),
		);
	};

	const keepHover = (
		isDatePicker: boolean,
		isHoverToRemove?: boolean,
	): void => {
		const allProducts: NodeListOf<Element> =
			document.querySelectorAll('.product-card-body');

		if (allProducts.length > 0) {
			let index = -1;

			for (const productCard of allProducts as any) {
				index++;

				if (index === productIndex) {
					if (isHoverToRemove) {
						if (isDatePicker) {
							productCard.style.transform = '';
							setIsToShowDatePicker(false);
						} else {
							setTimeout(() => (productCard.style.transform = ''), 400);
							setIsToShowSelect(false);
						}
					} else {
						if (isDatePicker) {
							setIsToShowDatePicker(true);

							if (isSelectOnFocus) {
								setIsSelectOnFocus(false);
								setTimeout(
									() =>
										(productCard.style.transform =
											'perspective(2000px) translateX(0)'),
									400,
								);
							} else
								productCard.style.transform =
									'perspective(2000px) translateX(0)';
						} else {
							productCard.style.transform = 'perspective(2000px) translateX(0)';
							setIsToShowSelect(true);
						}
					}
				}
			}
		} else setTimeout(() => keepHover(isDatePicker), 100);
	};

	const _handleSizesChange = (dataItem: ProductSize, value: number) => {
		if (product) {
			const normalizedQuantity = value ? value : 0;

			let editedSizes = formValues.sizes.map((entryItem) =>
				entryItem.sapCode === dataItem.sapCode
					? { ...dataItem, quantity: normalizedQuantity }
					: entryItem,
			);

			handleChangeMultiple({
				sizes: editedSizes,
				subTotal: orderTotalBySizes(editedSizes).amount,
			});
		}
	};

	return (
		<div className="bv-compact-form-container">
			<Row gutter={[12, 12]} className="bv-compact-form">
				<Col
					span={24}
					onClick={() => isEmbedded && !isToShowSelect && keepHover(false)}
					onBlur={() => isEmbedded && keepHover(false, true)}
				>
					<BVComboBox
						isEmbedded={isEmbedded}
						label={isEmbedded ? '' : 'Ship to:'}
						isToShow={isToShowSelect}
						data={
							facilities
								?.filter((facility) => facility.ecc === isEcc())
								.map((dataItem: CustomerFacility) => ({
									label: `${dataItem.name} - ${dataItem.address}, ${dataItem.city}`,
									value: dataItem.id,
								})) || []
						}
						className="facility-combobox"
						properties={{
							listHeight: 200,
							placeholder: isEmbedded ? 'Ship to' : 'Select a location...',
							disabled: facilitiesLoading,
							loading: facilitiesLoading,
							value: formValues.facility?.id,
							onKeyDown: (event: any) => {
								if (isEmbedded) event.stopPropagation();
							},
							onChange: (value: string) => {
								const facilityEntry = facilities?.find(
									(entry: CustomerFacility) => entry.id === parseInt(value),
								);

								handleChangeMultiple({
									facility: facilityEntry,
									deliveryDate: null,
								});

								if (isEmbedded) {
									keepHover(false, true);

									const elementOnFocus: any = document?.activeElement;

									if (
										elementOnFocus?.className ===
											'ant-select-selection-search-input' ||
										elementOnFocus?.role === 'combobox'
									)
										setIsSelectOnFocus(true);
								}
							},
						}}
					/>
				</Col>

				{product && product.isLimitedEdition && (
					<Col span={24}>
						<BVInput
							isEmbedded={isEmbedded}
							label={isEmbedded ? '' : 'Limited Edition Number:'}
							properties={{
								placeholder: isEmbedded
									? 'Limited Edition Number'
									: 'Limited edition Number...',
								value: formValues.limitedEditionCode,
								onKeyDown: (event: any) => {
									if (isEmbedded) event.stopPropagation();
								},
								onChange: (e: any) =>
									handleChange('limitedEditionCode', e.target.value),
							}}
						/>
					</Col>
				)}

				<Col
					span={isEmbedded ? 24 : 16}
					onClick={() => isEmbedded && keepHover(true)}
					onBlur={() => isEmbedded && keepHover(true, true)}
				>
					<BVDatePicker
						isEmbedded={isEmbedded}
						label={isEmbedded ? '' : 'Shipping date:'}
						infoMessage={!isEmbedded ? _handleFacilityAvailableDate() : ''}
						isToShow={isToShowDatePicker}
						properties={{
							placeholder: isEmbedded
								? 'Shipping date'
								: 'Select a shipping date...',
							defaultPickerValue: moment(availableDate),
							disabled:
								availableDateLoading ||
								facilitiesLoading ||
								!formValues.facility,
							disabledDate: (current: any) =>
								current &&
								current <
									moment(availableDate).subtract(1, 'days').endOf('day'),
							showToday: false,
							format: momentFormat,
							loading: availableDateLoading || facilitiesLoading,
							value: formValues.deliveryDate,
							onKeyDown: (event: any) => {
								event.preventDefault();
								event.stopPropagation();
								return false;
							},
							onChange: (date: any, dateString: string) =>
								handleChange('deliveryDate', date),
						}}
					/>
				</Col>
				<Col span={isEmbedded ? 24 : 8} style={{ marginBottom: '4px' }}>
					<BVShimmer
						loading={productSizesLoading}
						type={isEmbedded ? 'shimmer-oneline' : 'shimmer-details'}
						style={{ width: '100%' }}
					>
						{productSizes &&
							productSizes.map((dataItem, dataIndex) => (
								<BVInputNumber
									isEmbedded={isEmbedded}
									key={dataIndex}
									label={isEmbedded ? '' : 'Quantity:'}
									properties={{
										value: formValues.sizes[dataIndex]?.quantity || 0,
										step: 1,
										upHandler: <PlusOutlined />,
										downHandler: <MinusOutlined />,
										min: 0,
										onChange: (value: number) => {
											_handleSizesChange(dataItem, value);
										},
										onStep: (
											value: number,
											info: { offset: number; type: 'up' | 'down' },
										) => {
											_handleSizesChange(dataItem, value);
										},
									}}
								/>
							))}
					</BVShimmer>
				</Col>

				{!isEmbedded && <Col span={24}>{_handleFacilityExistingDates()}</Col>}

				<Col
					span={24}
					style={{
						display: 'flex',
						justifyContent: isEmbedded ? 'center' : 'flex-end',
					}}
				>
					{isEmbedded && (
						<Button
							style={{ marginRight: '10%', width: 'fit-content' }}
							type="primary"
							className="card-btn"
							onClick={() =>
								history.push(`/catalog/${businessUnitCode}/${product?.id}`)
							}
						>
							{'Discover'}
						</Button>
					)}

					<Button
						type="primary"
						disabled={
							facilitiesLoading ||
							!formValues.facility ||
							formValues.sizes.every((entry) => (entry.quantity || 0) === 0) ||
							upsertOrderLoading
						}
						onClick={_handleSubmitProduct}
					>
						{'Add to cart'}
					</Button>
				</Col>
			</Row>
		</div>
	);
};

export default BVCompactForm;
